"use client";

/**
 * P2-04 · <QuickAddButton> — a labelled control layered above the card link.
 * In the grid it reveals over the media on hover/focus and stays persistently
 * visible on touch (P2-DB04 §05 — no reveal that never comes); in the list it
 * is a compact, always-visible button. Disabled when out of stock.
 *
 * P14-10 · It now adds to the cart. For three phases it called an optional
 * `onQuickAdd` prop that no production surface ever passed, so every grid
 * shipped a button that did nothing at all — the flag was the only thing
 * standing between a customer and a control that silently ignored them.
 *
 * **Two products, two honest behaviours.** With exactly one enabled variant
 * there is nothing to ask, so one tap adds it. With several there is a real
 * question — which size, which colour — and the card is the wrong place to
 * answer it: the PDP owns the variant engine, its stock rules and its regional
 * pricing. So a multi-variant card says "Choose options" and goes there. It
 * never guesses a variant, which is the one outcome that would be worse than
 * the old no-op.
 *
 * (The ticket proposed a picker sheet "reusing" P9-11's. P9-11 shipped a
 * sticky *bar*, and P9-DB02 recorded that this repo has no sheet primitive —
 * so that was net-new UI rather than a reuse, and a cramped modal duplicating
 * the buy box is not obviously better than the page that already works.)
 *
 * P18-19 · The `quickAdd` flag is asked here, in a wrapper, rather than by the
 * card: the gate belongs with the control, and a shell that asks no flags needs
 * no hooks and can render on the server. The wrapper keeps the cart context out
 * of the tree entirely for a brand with the flag off.
 */
import * as React from "react";

import { useOptionalFeatureFlag } from "~/components/feature-flags/provider";
import { useCartContext } from "~/lib/cart/use-cart";
import { useOptionalToast } from "~/components/ui/toast";
import { useTranslator } from "~/i18n/provider";
import { LocaleLink } from "~/i18n/provider";
import { cn } from "~/lib/utils";

export interface QuickAddButtonProps {
  productTitle: string;
  productSlug: string;
  /** Null when the product has more than one enabled variant. */
  soleVariantId: string | null;
  inStock: boolean;
  /** Still honoured, for callers that want to react to a successful add. */
  onQuickAdd?: () => void;
  variant: "grid" | "list";
  className?: string;
}

function classesFor(variant: "grid" | "list", className?: string) {
  return cn(
    "z-[2] flex min-h-11 items-center justify-center gap-1 text-sm",
    "rounded-[var(--btn-radius)] [font-weight:var(--btn-weight)] [text-transform:var(--btn-uppercase)] tracking-[var(--btn-tracking)]",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2",
    "disabled:pointer-events-none disabled:opacity-50",
    variant === "grid"
      ? cn(
          // No `w-full`: the grid card places this with `absolute inset-x-2`, and
          // width:100% on top of left+right overrides the right inset — the
          // button ran 8 px past the image box and was clipped by the card border.
          "border border-border bg-surface/95 text-fg shadow-sm transition-opacity hover:bg-surface",
          // Hover-revealed on fine pointers, keyboard-revealed via
          // focus-within, persistently visible on touch.
          "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 pointer-coarse:opacity-100",
        )
      : "bg-primary px-4 text-primary-fg hover:brightness-95",
    className,
  );
}

export function QuickAddButton(props: QuickAddButtonProps) {
  const enabled = useOptionalFeatureFlag("quickAdd");
  if (!enabled) return null;
  return <QuickAddControl {...props} />;
}

function QuickAddControl({
  productTitle,
  productSlug,
  soleVariantId,
  inStock,
  onQuickAdd,
  variant,
  className,
}: QuickAddButtonProps) {
  const t = useTranslator();
  // Nullable: `/kitchen-sink` renders cards outside the storefront shell, and
  // a missing toaster should cost a notification rather than the page.
  const toaster = useOptionalToast();
  // Null outside a CartProvider — the kitchen sink renders cards bare. A card
  // that cannot reach the cart falls back to the PDP rather than throwing.
  const cart = useCartContext();
  const [busy, setBusy] = React.useState(false);

  // Visible text differs by density; the ACCESSIBLE name does not. A screen
  // reader user moving between a grid and a list is meeting the same control,
  // and renaming it per layout would say otherwise.
  const text = variant === "grid" ? t("quickAdd.quickAdd") : t("quickAdd.add");
  const label = `${t("quickAdd.quickAdd")} ${productTitle}`;

  // More than one variant (or no cart context): send them where the question
  // can actually be answered.
  if (!soleVariantId || !cart) {
    if (!inStock) {
      return (
        <button
          type="button"
          disabled
          aria-label={label}
          className={classesFor(variant, className)}
        >
          <span aria-hidden>+</span>
          {text}
        </button>
      );
    }
    return (
      <LocaleLink
        href={`/products/${productSlug}`}
        aria-label={`${t("quickAdd.chooseOptions")} — ${productTitle}`}
        className={classesFor(variant, className)}
      >
        {t("quickAdd.chooseOptions")}
      </LocaleLink>
    );
  }

  return (
    <button
      type="button"
      disabled={!inStock || busy}
      aria-label={label}
      onClick={async (e) => {
        // The whole card is a link; adding to the cart must not navigate.
        e.preventDefault();
        e.stopPropagation();
        setBusy(true);
        try {
          await cart.addItem({
            variantId: soleVariantId,
            quantity: 1,
            personalisation: null,
          });
          toaster?.toast({ title: t("quickAdd.added"), tone: "success" });
          onQuickAdd?.();
        } catch {
          // Never a silent no-op — that was the old behaviour, and the reason
          // this ticket exists.
          toaster?.toast({ title: t("quickAdd.failed"), tone: "danger" });
        } finally {
          setBusy(false);
        }
      }}
      className={classesFor(variant, className)}
    >
      <span aria-hidden>+</span>
      {text}
    </button>
  );
}
